import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface WizardStepProps {
  title: string
  description?: string
  children: ReactNode
  className?: string
}

export function WizardStep({ title, description, children, className }: WizardStepProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      <div>{children}</div>
    </div>
  )
}

interface WizardProgressProps {
  currentStep: number
  totalSteps: number
}

export function WizardProgress({ currentStep, totalSteps }: WizardProgressProps) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div key={index} className="flex items-center">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
              index < currentStep
                ? 'bg-success-500 text-white'
                : index === currentStep
                  ? 'border-2 border-primary-500 bg-primary-50 text-primary-600'
                  : 'border-2 border-gray-300 text-gray-400'
            )}
          >
            {index + 1}
          </div>
          {index < totalSteps - 1 && (
            <div
              className={cn(
                'ml-2 h-0.5 w-8 transition-colors',
                index < currentStep ? 'bg-success-500' : 'bg-gray-300'
              )}
            />
          )}
        </div>
      ))}
    </div>
  )
}

interface WizardActionsProps {
  onBack?: () => void
  onNext?: () => void
  onSkip?: () => void
  nextLabel?: string
  backLabel?: string
  skipLabel?: string
  isNextDisabled?: boolean
  isNextLoading?: boolean
  showBack?: boolean
  showSkip?: boolean
}

export function WizardActions({
  onBack,
  onNext,
  onSkip,
  nextLabel = 'Continue',
  backLabel = 'Back',
  skipLabel = 'Skip',
  isNextDisabled = false,
  isNextLoading = false,
  showBack = true,
  showSkip = false,
}: WizardActionsProps) {
  return (
    <div className="flex items-center justify-between pt-4">
      <div>
        {showBack && onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            {backLabel}
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        {showSkip && onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            {skipLabel}
          </button>
        )}
        {onNext && (
          <button
            type="button"
            onClick={onNext}
            disabled={isNextDisabled || isNextLoading}
            className={cn(
              'rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isNextLoading ? 'Loading...' : nextLabel}
          </button>
        )}
      </div>
    </div>
  )
}
